import * as THREE from 'three'
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import type {
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
} from '@/plugins/gsplat/types/PluginTypes'
import {
  GSplatResourceBase,
  type GSplatResourceDataSource,
} from '@/plugins/gsplat/unified/resource/GSplatResourceBase'
import type { AssetId, Disposable } from '@/shared/types/core'
import type { GSplatWorkBufferModifier } from '@/shared/types/gsplat'
import {
  buildGSplatWeatherPlan,
  type GSplatWeatherGridConfig,
  type GSplatWeatherPlan,
  GSplatWeatherPlanError,
  snapGSplatWeatherBaseCell,
} from './GSplatWeatherPlan'
import { GSPLAT_WEATHER_READ_GLSL, GSPLAT_WEATHER_READ_WGSL } from './GSplatWeatherShaders'

export interface GSplatWeatherOptions {
  readonly id?: AssetId
  readonly name?: string
  readonly extents?: THREE.Vector3
  readonly density?: number
  readonly speed?: number
  readonly offset?: number
  readonly fall?: number
  readonly wrap?: number
  readonly drift?: number
  readonly fade?: number
  readonly size?: number
  readonly opacity?: number
  readonly seed?: number
  readonly elongation?: number
  readonly basePosition?: THREE.Vector3
  readonly followTarget?: GSplatWeatherFollowTarget | null
}

export interface GSplatWeatherFollowTarget {
  getPosition(): THREE.Vector3 | null
}

export interface GSplatWeatherParameters {
  readonly uWeatherTime: number
  readonly uWeatherBaseCell: THREE.Vector3
  readonly uWeatherCellSize: number
  readonly uWeatherGridHalf: THREE.Vector3
  readonly uWeatherSpeed: number
  readonly uWeatherOffset: number
  readonly uWeatherFall: number
  readonly uWeatherWrap: number
  readonly uWeatherDrift: number
  readonly uWeatherFade: number
  readonly uWeatherSize: number
  readonly uWeatherOpacity: number
  readonly uWeatherSeed: number
  readonly uWeatherElongation: number
}

export interface GSplatWeatherRuntime {
  readonly graphicsAdapter: GSplatGraphicsAdapter
  readonly onVisualActivity?: (reason: string) => void
  createWithResource(
    request: Readonly<GaussianSplatLoadRequest>,
    resource: GSplatResourceLease
  ): GaussianSplatHandle
  replaceWithResource(
    id: AssetId,
    resource: GSplatResourceLease,
    metadata?: { readonly url?: string; readonly name?: string }
  ): GaussianSplatHandle
  mount(handle: GaussianSplatHandle): void
  disposeAsset(id: AssetId): void
  setParameters(id: AssetId, parameters: ReadonlyMap<string, unknown>): void
  assertResourceCount?(count: number): void
}

type GSplatWeatherResourceData = GSplatResourceDataSource

export class GSplatWeatherResource extends GSplatResourceBase<GSplatWeatherResourceData> {
  readonly kind = 'container' as const

  constructor(adapter: GSplatGraphicsAdapter, plan: GSplatWeatherPlan) {
    const dimensions = adapter.calculateTextureDimensions(plan.count)
    const textureLimit = adapter.limits.maxTextureDimension2D
    if (dimensions.width > textureLimit || dimensions.height > textureLimit) {
      throw new GSplatWeatherPlanError(
        `Weather texture dimension 超过限制: actual=${dimensions.width}x${dimensions.height}, limit=${textureLimit}`,
        {
          name: 'maxTextureDimension2D',
          actual: Math.max(dimensions.width, dimensions.height),
          limit: textureLimit,
        }
      )
    }
    const bytesPerTexel = adapter.getTextureFormatInfo('rgba8unorm').bytesPerTexel
    const paddedBytes = dimensions.width * dimensions.height * bytesPerTexel
    if (paddedBytes > adapter.limits.maxBufferSize) {
      throw new GSplatWeatherPlanError(
        `Weather texture bytes 超过限制: actual=${paddedBytes}, limit=${adapter.limits.maxBufferSize}`,
        {
          name: 'maxBufferSize',
          actual: paddedBytes,
          limit: adapter.limits.maxBufferSize,
        }
      )
    }
    const format = new GSplatFormat(adapter, [{ name: 'dataColor', format: 'rgba8unorm' }], {
      readGLSL: GSPLAT_WEATHER_READ_GLSL,
      readWGSL: GSPLAT_WEATHER_READ_WGSL,
    })
    const data: GSplatWeatherResourceData = {
      numSplats: plan.count,
      getCenters: () => plan.centers,
      calcAabb: (target) => target.copy(plan.aabb),
    }
    super({
      adapter,
      data,
      format,
      getCpuExportData: () => null,
    })

    const streams = new GSplatStreams(adapter)
    try {
      streams.init(format, plan.count)
      this.attachStreams(streams)
      const texture = this.getTexture('dataColor')
      if (!texture) throw new Error('GSplatWeather dataColor texture 创建失败')
      const upload = new Uint8Array(texture.width * texture.height * 4)
      upload.set(plan.data)
      adapter.updateTexture(texture, upload)
    } catch (error) {
      streams.destroy()
      this.destroy()
      throw error
    }
  }
}

/** Deterministic Weather controller foundation; resource replacement is owned by GSplatSystem. */
export class GSplatWeather {
  private planValue: GSplatWeatherPlan
  private timeValue = 0
  private baseCellValue: readonly [number, number, number]
  private speedValue: number
  private offsetValue: number
  private fallValue: number
  private wrapValue: number
  private driftValue: number
  private fadeValue: number
  private sizeValue: number
  private opacityValue: number
  private seedValue: number
  private elongationValue: number

  constructor(options: GSplatWeatherOptions = {}) {
    this.speedValue = nonNegative(options.speed ?? 1, 'speed')
    this.offsetValue = finite(options.offset ?? 0, 'offset')
    this.fallValue = nonNegative(options.fall ?? 4, 'fall')
    this.wrapValue = clamp01(options.wrap ?? 1, 'wrap')
    this.driftValue = nonNegative(options.drift ?? 0.5, 'drift')
    this.fadeValue = clamp01(options.fade ?? 0.25, 'fade')
    this.sizeValue = positive(options.size ?? 0.05, 'size')
    this.opacityValue = clamp01(options.opacity ?? 1, 'opacity')
    this.seedValue = finite(options.seed ?? 0, 'seed')
    this.elongationValue = positive(options.elongation ?? 1, 'elongation')
    this.planValue = buildGSplatWeatherPlan(this.gridConfig(options), {
      basePosition: options.basePosition,
    })
    this.baseCellValue = this.planValue.baseCell
  }

  get plan(): GSplatWeatherPlan {
    return this.planValue
  }

  get parameters(): GSplatWeatherParameters {
    return {
      uWeatherTime: this.timeValue,
      uWeatherBaseCell: new THREE.Vector3(...this.baseCellValue),
      uWeatherCellSize: this.planValue.cellSize,
      uWeatherGridHalf: new THREE.Vector3(...this.planValue.halfCells),
      uWeatherSpeed: this.speedValue,
      uWeatherOffset: this.offsetValue,
      uWeatherFall: this.fallValue,
      uWeatherWrap: this.wrapValue,
      uWeatherDrift: this.driftValue,
      uWeatherFade: this.fadeValue,
      uWeatherSize: this.sizeValue,
      uWeatherOpacity: this.opacityValue,
      uWeatherSeed: this.seedValue,
      uWeatherElongation: this.elongationValue,
    }
  }

  get readGLSL(): string {
    return GSPLAT_WEATHER_READ_GLSL
  }

  get readWGSL(): string {
    return GSPLAT_WEATHER_READ_WGSL
  }

  get workBufferModifier(): GSplatWorkBufferModifier | null {
    return null
  }

  createResource(adapter: GSplatGraphicsAdapter): GSplatResourceLease {
    const resource = new GSplatWeatherResource(adapter, this.planValue)
    const lease = resource.acquire()
    resource.destroy()
    return lease
  }

  get needsContinuousActivity(): boolean {
    return this.opacityValue > 0 && (this.speedValue !== 0 || this.driftValue !== 0)
  }

  setBasePosition(position: THREE.Vector3): boolean {
    const next = snapGSplatWeatherBaseCell(position, this.planValue.cellSize)
    if (
      next[0] === this.baseCellValue[0] &&
      next[1] === this.baseCellValue[1] &&
      next[2] === this.baseCellValue[2]
    ) {
      return false
    }
    this.baseCellValue = next
    return true
  }

  setElapsedTime(seconds: number): void {
    this.timeValue = finite(seconds, 'time')
  }

  update(delta: number): void {
    if (!Number.isFinite(delta)) throw new Error(`GSplatWeather delta 必须是有限数: ${delta}`)
    this.timeValue += Math.max(0, delta)
  }

  rebuild(config: GSplatWeatherGridConfig, basePosition = new THREE.Vector3()): void {
    const next = buildGSplatWeatherPlan(config, { basePosition })
    this.planValue = next
    this.baseCellValue = next.baseCell
  }

  private gridConfig(options: GSplatWeatherOptions): GSplatWeatherGridConfig {
    return {
      extents: options.extents?.clone() ?? new THREE.Vector3(8, 8, 8),
      density: options.density ?? 2,
    }
  }
}

/**
 * System-owned procedural Weather generator.
 *
 * The data controller above remains usable without a System. This wrapper owns the
 * Resource/Placement Handle lifecycle and publishes one immutable parameter snapshot
 * per update, so WebGL and WebGPU WorkBuffer source commands consume the same values.
 */
export class GSplatWeatherController implements Disposable {
  private readonly id?: AssetId
  private readonly name: string
  private readonly runtime: GSplatWeatherRuntime
  private readonly onDispose?: (controller: GSplatWeatherController) => void
  private optionsValue: GSplatWeatherOptions
  private weatherValue: GSplatWeather
  private handleValue: GaussianSplatHandle | null = null
  private initialized = false
  private disposed = false
  private generation = 0
  private lastPublishedTime: number | null = null
  private lastPublishedBaseCell: readonly [number, number, number] | null = null
  private errorValue: Error | null = null

  constructor(
    runtime: GSplatWeatherRuntime,
    options: GSplatWeatherOptions = {},
    onDispose?: (controller: GSplatWeatherController) => void
  ) {
    this.runtime = runtime
    this.id = options.id
    this.name = options.name ?? 'GSplatWeather'
    this.optionsValue = cloneWeatherOptions(options)
    this.weatherValue = new GSplatWeather(this.optionsValue)
    this.onDispose = onDispose
  }

  get handle(): GaussianSplatHandle | null {
    return this.handleValue
  }

  get weather(): GSplatWeather {
    return this.weatherValue
  }

  get plan(): GSplatWeatherPlan {
    return this.weatherValue.plan
  }

  get lastError(): Error | null {
    return this.errorValue
  }

  get needsContinuousActivity(): boolean {
    return (
      this.runtime.graphicsAdapter.backend === 'webgpu' && this.weatherValue.needsContinuousActivity
    )
  }

  initialize(): void {
    this.assertActive()
    this.initialized = true
  }

  postUpdate(delta = 0): void {
    this.assertActive()
    if (!this.initialized) this.initialize()
    try {
      const target = this.optionsValue.followTarget?.getPosition() ?? null
      const baseChanged = target ? this.weatherValue.setBasePosition(target) : false
      if (baseChanged && target) {
        this.optionsValue = Object.freeze({
          ...this.optionsValue,
          basePosition: target.clone(),
        })
      }
      const animated =
        this.runtime.graphicsAdapter.backend === 'webgpu' &&
        this.weatherValue.needsContinuousActivity &&
        Number.isFinite(delta) &&
        delta !== 0
      if (animated) this.weatherValue.update(delta)
      if (!this.handleValue) {
        this.commitResource(++this.generation)
        return
      }
      if (
        baseChanged ||
        animated ||
        this.lastPublishedTime !== this.weatherValue.parameters.uWeatherTime ||
        !sameBaseCell(this.lastPublishedBaseCell, this.weatherValue.plan.baseCell)
      ) {
        this.publishParameters()
        if (!animated || baseChanged) this.notifyVisualActivity()
      }
      this.errorValue = null
    } catch (error) {
      this.errorValue = error instanceof Error ? error : new Error(String(error))
    }
  }

  setBasePosition(position: THREE.Vector3): boolean {
    this.assertActive()
    const changed = this.weatherValue.setBasePosition(position)
    if (changed) {
      this.optionsValue = Object.freeze({
        ...this.optionsValue,
        basePosition: position.clone(),
      })
      if (this.handleValue) this.publishParameters()
      this.notifyVisualActivity()
    }
    return changed
  }

  setElapsedTime(seconds: number): void {
    this.assertActive()
    if (this.weatherValue.parameters.uWeatherTime === seconds) return
    this.weatherValue.setElapsedTime(seconds)
    if (this.handleValue) {
      this.publishParameters()
      this.notifyVisualActivity()
    }
  }

  rebuild(options: GSplatWeatherOptions = {}): void {
    this.assertActive()
    const nextOptions = mergeWeatherOptions(this.optionsValue, options)
    const nextWeather = new GSplatWeather(nextOptions)
    const operationGeneration = ++this.generation
    const previousWeather = this.weatherValue
    const previousOptions = this.optionsValue
    this.weatherValue = nextWeather
    this.optionsValue = nextOptions
    try {
      if (this.handleValue) {
        this.commitResource(operationGeneration)
      } else {
        this.lastPublishedTime = null
        this.lastPublishedBaseCell = null
        this.notifyVisualActivity()
      }
      this.errorValue = null
    } catch (error) {
      this.weatherValue = previousWeather
      this.optionsValue = previousOptions
      this.errorValue = error instanceof Error ? error : new Error(String(error))
      throw this.errorValue
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.generation++
    const handle = this.handleValue
    this.handleValue = null
    let firstError: unknown
    if (handle) {
      try {
        this.runtime.disposeAsset(handle.id)
      } catch (error) {
        firstError = error
      }
    }
    try {
      this.onDispose?.(this)
    } catch (error) {
      firstError ??= error
    }
    if (firstError) throw firstError
  }

  private commitResource(operationGeneration: number): void {
    if (this.disposed || this.generation !== operationGeneration) {
      throw new Error('GSplatWeather rebuild 已失效')
    }
    this.runtime.assertResourceCount?.(this.weatherValue.plan.count)
    const resource = this.weatherValue.createResource(this.runtime.graphicsAdapter)
    let consumed = false
    let createdHandle: GaussianSplatHandle | null = null
    let createdNewHandle = false
    try {
      const handle = this.handleValue
        ? this.runtime.replaceWithResource(this.handleValue.id, resource, { name: this.name })
        : this.runtime.createWithResource(
            Object.freeze({
              url: `procedural://gsplat-weather/${this.id ?? 'generated'}`,
              name: this.name,
              ...(this.id ? { id: this.id } : {}),
            }),
            resource
          )
      consumed = true
      createdHandle = handle
      createdNewHandle = !this.handleValue
      if (createdNewHandle) this.runtime.mount(handle)
      this.handleValue = handle
      this.publishParameters()
      this.notifyVisualActivity()
    } catch (error) {
      if (createdNewHandle && createdHandle) {
        try {
          this.runtime.disposeAsset(createdHandle.id)
        } catch {
          // Keep the original commit error; System owns the cleanup diagnostic.
        }
        this.handleValue = null
      }
      throw error
    } finally {
      if (!consumed) resource.dispose()
    }
  }

  private publishParameters(): void {
    const parameters = this.weatherValue.parameters
    const values = new Map<string, unknown>(
      Object.entries(parameters).map(([name, value]) => [name, value])
    )
    if (!this.handleValue) return
    this.runtime.setParameters(this.handleValue.id, values)
    this.lastPublishedTime = parameters.uWeatherTime
    this.lastPublishedBaseCell = this.weatherValue.plan.baseCell
  }

  private notifyVisualActivity(): void {
    this.runtime.onVisualActivity?.('gsplat.weather')
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('GSplatWeather 已销毁')
  }
}

function sameBaseCell(
  left: readonly [number, number, number] | null,
  right: readonly [number, number, number]
): boolean {
  return Boolean(left && left[0] === right[0] && left[1] === right[1] && left[2] === right[2])
}

function cloneWeatherOptions(options: GSplatWeatherOptions): GSplatWeatherOptions {
  return Object.freeze({
    ...options,
    ...(options.extents ? { extents: options.extents.clone() } : {}),
    ...(options.basePosition ? { basePosition: options.basePosition.clone() } : {}),
  })
}

function mergeWeatherOptions(
  base: GSplatWeatherOptions,
  next: GSplatWeatherOptions
): GSplatWeatherOptions {
  return cloneWeatherOptions({
    ...base,
    ...next,
    ...(next.extents ? { extents: next.extents.clone() } : {}),
    ...(next.basePosition ? { basePosition: next.basePosition.clone() } : {}),
  })
}

function finite(value: number, name: string): number {
  if (!Number.isFinite(value)) throw new Error(`GSplatWeather ${name} 必须是有限数: ${value}`)
  return value
}

function nonNegative(value: number, name: string): number {
  const normalized = finite(value, name)
  if (normalized < 0) throw new Error(`GSplatWeather ${name} 不能为负数`)
  return normalized
}

function positive(value: number, name: string): number {
  const normalized = finite(value, name)
  if (normalized <= 0) throw new Error(`GSplatWeather ${name} 必须为正数`)
  return normalized
}

function clamp01(value: number, name: string): number {
  const normalized = finite(value, name)
  if (normalized < 0 || normalized > 1) {
    throw new Error(`GSplatWeather ${name} 必须位于 [0,1]`)
  }
  return normalized
}
